import { createContext, useContext, useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useAccount, useConnect, useDisconnect } from 'wagmi';
import { signMessage } from '@wagmi/core';

import { SIGNATURE_EXPIRY_TIME, AUTH_MESSAGE_TO_SIGN } from 'constants/index';
import { config } from 'constants/walletConfig';
import userService from 'services/user';
import useNotification from 'hooks/useNotification';

const WalletContext = createContext();
export const useWallet = () => useContext(WalletContext);

/********************  Context Provider ********************/
export const WalletProvider = ({ children }) => {
  const { address, status } = useAccount();
  const { connectors } = useConnect();
  const { disconnect } = useDisconnect();
  const showNotification = useNotification();

  const [wallet, setWallet] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [twitterUser, setTwitterUser] = useState(null);
  const [walletModalOpen, setWalletModalOpen] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [cookies, setCookie] = useCookies(['user']);

  const metamask = connectors?.find((connector) => connector.name === 'MetaMask');

  useEffect(() => {
    const token = localStorage.getItem('token');
    const tokenExpiry = parseInt(localStorage.getItem('token_expiry'), 10) || 0;
    const tokenWallet = localStorage.getItem('token_wallet');
    const now = Date.now();
    const isTokenExpired = tokenExpiry < now;

    const authenticateWallet = async () => {
      // Prevent multiple simultaneous authentication attempts
      if (isAuthenticating) {
        console.log('Authentication already in progress, skipping...');
        return;
      }

      setIsAuthenticating(true);

      try {
        const walletAddress = address.toLowerCase();
        const nonceData = await userService.getNonce(walletAddress);
        if (nonceData) {
          const message = AUTH_MESSAGE_TO_SIGN(walletAddress, nonceData.data.nonce);
          const sign = await signMessage(config, {
            account: walletAddress,
            message,
          });

          const authResponseData = await userService.authenticate(
            walletAddress,
            sign,
            localStorage.getItem('linked_main_wallet'),
          );
          if (authResponseData) {
            localStorage.setItem('token', authResponseData.data.auth.token);
            localStorage.setItem('token_wallet', walletAddress);
            localStorage.setItem('token_expiry', Date.now() + SIGNATURE_EXPIRY_TIME);
            localStorage.setItem('linked_wallets', JSON.stringify(authResponseData.data.linked));
            localStorage.setItem('linked_main_wallet', authResponseData.data.mainwallet);
            authResponseData.data.twitterUser &&
              localStorage.setItem('twitter', authResponseData.data.twitterUser);
            setAuthenticatedState();
            showNotification({
              type: 'success',
              message: 'Wallet connected successfully',
            });
          }
        }
      } catch (e) {
        console.error('Authentication error:', e);
        setIsAuthenticated(false);

        // Check if user rejected the signature request
        const isUserRejection =
          e?.message?.toLowerCase().includes('user rejected') ||
          e?.message?.toLowerCase().includes('user denied') ||
          e?.code === 4001 ||
          e?.code === 'ACTION_REJECTED';

        if (isUserRejection) {
          // User cancelled - disconnect wallet completely
          disconnect();
          // showNotification({
          //   type: 'info',
          //   message: 'Wallet connection cancelled',
          // });
        } else {
          // Other error (network, etc.)
          showNotification({
            type: 'error',
            message: 'Wallet connection failed, please try again',
          });
        }

        localStorage.removeItem('token');
        localStorage.removeItem('token_wallet');
        localStorage.removeItem('token_expiry');
        localStorage.removeItem('twitter');
      } finally {
        // Always clear the authenticating flag
        setIsAuthenticating(false);
      }
    };

    const setAuthenticatedState = () => {
      setWallet(localStorage.getItem('token_wallet'));
      localStorage.getItem('twitter') && setTwitterUser(localStorage.getItem('twitter'));
      setIsAuthenticated(true);
    };

    if (address && !cookies.address) {
      setCookie('address', address.toLowerCase(), { path: '/' });
    }

    // Don't reset authentication state if we're in the middle of authenticating
    if (!isAuthenticating) {
      if (status === 'disconnected') {
        if (!token || isTokenExpired) {
          setIsAuthenticated(false);
          return;
        }

        setAuthenticatedState();
        return;
      }

      if (status === 'connected') {
        if (!token || isTokenExpired || tokenWallet !== address.toLowerCase()) {
          // Only set to false if we're not already authenticating
          setIsAuthenticated(false);
          authenticateWallet();
          return;
        }

        setAuthenticatedState();
      }
    }
  }, [status, address, isAuthenticating]);

  const connectWallet = async () => {
    try {
      if (metamask) {
        await metamask.connect();
      } else {
        setWalletModalOpen(true);
      }
    } catch (error) {
      showNotification({
        type: 'error',
        message: 'Please try again',
      });
      console.error(error);
    }
  };

  const closeModal = () => setWalletModalOpen(false);

  const disconnectWallet = () => {
    // Clear all authentication data
    localStorage.removeItem('token');
    localStorage.removeItem('token_wallet');
    localStorage.removeItem('token_expiry');
    localStorage.removeItem('twitter');
    localStorage.removeItem('linked_wallets');
    localStorage.removeItem('linked_main_wallet');

    // Clear cookies
    setCookie('address', '', { path: '/', maxAge: 0 });

    // Reset state
    setWallet(null);
    setIsAuthenticated(false);
    setTwitterUser(null);

    // Disconnect from wallet provider
    disconnect();

    showNotification({
      type: 'success',
      message: 'Wallet disconnected successfully',
    });
  };

  return (
    <WalletContext.Provider
      value={{
        isAuthenticated,
        isAuthenticating,
        wallet,
        twitterUser,
        walletModalOpen,
        connectWallet,
        disconnectWallet,
        closeModal,
      }}
    >
      {children}
    </WalletContext.Provider>
  );
};
